import React, { useState, useEffect, useRef } from 'react';

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                         GARDEN GURUS GROW                                 ║
 * ║                  Complete App - April 2026 (v3)                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  WHAT CHANGED FROM v2:                                                    ║
 * ║  - URL routing with proper paths (/gurus, /discover, /give, etc.)         ║
 * ║  - Browser back/forward button works                                      ║
 * ║  - 'Gives' -> 'Give' everywhere                                           ║
 * ║  - New /gurus directory page (full grid of all gurus)                     ║
 * ║  - 'Browse all gurus' button on home page                                 ║
 * ║  - New nav: Gurus, Discover, Give, Become, Connect                        ║
 * ║  - New hero: 'There's organic. And then there's homegrown.'               ║
 * ║  - New Discover bio (mission-first, wholesome voice)                      ║
 * ║  - Garden Application form on /give page                                  ║
 * ║  - Donation Request form on /give page                                    ║
 * ║  - Updated Connect form: 4 categories                                     ║
 * ║  ---                                                                      ║
 * ║  WHAT CHANGED FROM v1:                                                    ║
 * ║  - Fixed broken page-hero styles (Discover/Gives/Become/Connect)          ║
 * ║  - Added Crystal's photo to Discover page                                 ║
 * ║  - Slightly bigger text throughout                                        ║
 * ║  - Removed booking flow, replaced with direct guru-message form           ║
 * ║  - Real validation (email, phone, length, sanitization)                   ║
 * ║  - Honeypot spam protection on every form                                 ║
 * ║  - Rate limiting (30 sec between submissions per form)                    ║
 * ║  - Fixed missing handleSubmit in Become form                              ║
 * ║  - "Set your own rates - keep 100%" messaging                             ║
 * ║  - Linked Record support for Target Guru in inquiries                     ║
 * ║                                                                           ║
 * ║  QUICK NAVIGATION (Cmd+F / Ctrl+F):                                       ║
 * ║                                                                           ║
 * ║  [CONFIG]      - Images, colors                                           ║
 * ║  [FILTERS]     - >>> EDIT FILTER LIST HERE <<<                            ║
 * ║  [AIRTABLE]    - API key + table names                                    ║
 * ║  [VALIDATION]  - Form rules                                               ║
 * ║  [API]         - Airtable functions                                       ║
 * ║  [HOME]        - Find a Guru                                              ║
 * ║  [DISCOVER]    - About / Mission                                          ║
 * ║  [GIVES]       - Nonprofit                                                ║
 * ║  [BECOME]      - Become a Guru                                            ║
 * ║  [CONNECT]     - Contact form                                             ║
 * ║  [STYLES]      - All CSS                                                  ║
 * ║                                                                           ║
 * ║  >>> AIRTABLE SETUP REQUIRED BEFORE THIS WORKS <<<                        ║
 * ║  1. INQUIRIES table needs a "Target Guru" field                           ║
 * ║     Type: Linked Record → linked to GURUS table                           ║
 * ║  2. GURUS table needs an "Email" field for the automation                 ║
 * ║  3. Airtable Automation: see deployment notes Crystal received            ║
 * ║                                                                           ║
 * ║  >>> SECURITY: API key is client-exposed. Migrate to                      ║
 * ║  Netlify Functions within 24-48 hours of launch. <<<                      ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

// ============================================================================
// [CONFIG] - IMAGES & BRAND COLORS
// ============================================================================

const IMAGES = {
  // Logo (base64 encoded)
  logo: 'data:image/webp;base64,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',

  // Botanical decorations from Cloudinary
  centerFlower: 'https://res.cloudinary.com/dngfsmbnj/image/upload/v1769555225/LeftSideFlowerBig_nbvsz0.png',
  bottomLeftCorner: 'https://res.cloudinary.com/dngfsmbnj/image/upload/v1769555246/BottomLEftCornerFlower_qphtbk.png',
  floatingPads: 'https://res.cloudinary.com/dngfsmbnj/image/upload/v1769555208/FloatingPads_cv0cy5.png',
  heroBg: 'https://res.cloudinary.com/dngfsmbnj/image/upload/v1776396218/A621F46A-F9F5-445C-B2ED-74BC3AEC8D44_i1mmkl.png',

  // Crystal's photo on Discover page
  crystalPhoto: 'https://res.cloudinary.com/dngfsmbnj/image/upload/v1777377832/Crystal_in_Front_Yard_Garden_JAN_2026-7_x8aipz.jpg',
};

const COLORS = {
  sage: '#7C9070',
  terracotta: '#C4977A',
  cream: '#FAF8F5',
  charcoal: '#2C2C2C',
  warmBrown: '#8B7355',
  lightSage: 'rgba(124, 144, 112, 0.15)',
  lightBrown: 'rgba(139, 115, 85, 0.15)',
};

// ============================================================================
// [FILTERS] - >>> EDIT FILTER LIST HERE <<<
// ============================================================================

/**
 * ╔════════════════════════════════════════════════════════════════════╗
 * ║  HOW TO EDIT THE HOME PAGE FILTER PILLS                            ║
 * ║                                                                    ║
 * ║  TO ADD A FILTER: Add a new line in the SPECIALTIES array below.   ║
 * ║  TO REMOVE A FILTER: Delete the line.                              ║
 * ║  TO REORDER: Cut and paste lines into the order you want.          ║
 * ║                                                                    ║
 * ║  IMPORTANT: The text must EXACTLY match what you put in            ║
 * ║  Airtable's "Primary Specialty" or "Tags" fields - capitalization, ║
 * ║  spaces, and dashes all matter. Otherwise filtering won't work     ║
 * ║  for that category.                                                ║
 * ║                                                                    ║
 * ║  "All" must always stay as the first item.                         ║
 * ╚════════════════════════════════════════════════════════════════════╝
 */

const SPECIALTIES = [
  "All",
  "Aquaponics",
  "Beekeeping",
  "Community Garden",
  "Container Gardens",
  "Drought-Tolerant",
  "Food Forests",
  "Food Preservation",
  "General Garden",
  "Hydroponics",
  "Permaculture",
  "Soil and Compost",
];

/**
 * The dropdown options on the Become a Guru application form.
 * Edit the same way as SPECIALTIES above.
 */
const APPLICATION_SPECIALTIES = [
  "Aquaponics",
  "Beekeeping",
  "Community Garden",
  "Container Gardens",
  "Drought-Tolerant",
  "Food Forests",
  "Food Preservation",
  "General Garden",
  "Hydroponics",
  "Medicinal Herbs",
  "Permaculture",
  "Soil and Compost",
  "Other",
];

// ============================================================================
// [AIRTABLE] - DATABASE CONFIG
// ============================================================================

/**
 * SECURITY WARNING: This API key is exposed in client-side code.
 * Anyone visiting your site can extract it from the page source.
 *
 * Migrate to Netlify Functions within 24-48 hours of launch:
 * 1. Move this config to a Netlify Function
 * 2. Update API calls below to hit /api/airtable instead
 * 3. Generate new Airtable key and revoke this one
 */
/**
 * SECURITY: API key is no longer in client code.
 * It now lives in Netlify environment variables and is only used by
 * the Netlify Function at netlify/functions/airtable.js
 *
 * All Airtable requests go through that middleman, which keeps the
 * key server-side where browser visitors can't see it.
 */
const AIRTABLE_PROXY = '/.netlify/functions/airtable';

const TABLES = {
  gurus: 'GURUS',
  inquiries: 'INQUIRIES',
  applications: 'GURU_APPLICATIONS',
  givesApplications: 'GIVES_APPLICATIONS',
  donationInterests: 'DONATION_INTERESTS',
};

// ============================================================================
// [VALIDATION] - FORM RULES & SECURITY HELPERS
// ============================================================================

const Validators = {
  name: (val) => {
    const v = (val || '').trim();
    if (!v) return 'Name is required';
    if (v.length < 2) return 'Please enter your full name';
    if (v.length > 100) return 'Name is too long';
    return '';
  },
  email: (val) => {
    const v = (val || '').trim();
    if (!v) return 'Email is required';
    if (v.length > 254) return 'Email is too long';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Please enter a valid email';
    return '';
  },
  phone: (val) => {
    const digits = (val || '').replace(/\D/g, '');
    if (!digits) return 'Phone number is required';
    if (digits.length < 10) return 'Please enter a 10-digit phone number';
    if (digits.length > 15) return 'Phone number is too long';
    return '';
  },
  message: (val, min = 10) => {
    const v = (val || '').trim();
    if (!v) return 'Please write a message';
    if (v.length < min) return `Please write at least ${min} characters`;
    if (v.length > 2000) return 'Message is too long (max 2000 characters)';
    return '';
  },
  location: (val) => {
    const v = (val || '').trim();
    if (!v) return 'Location is required';
    if (v.length < 2) return 'Please enter a valid location';
    if (v.length > 100) return 'Location is too long';
    return '';
  },
  selection: (val, label = 'Please make a selection') => {
    if (!val || !val.toString().trim()) return label;
    return '';
  },
  phoneOptional: (val) => {
    if (!val || !val.trim()) return '';
    const digits = val.replace(/\D/g, '');
    if (digits.length < 10) return 'Please enter a 10-digit phone number';
    if (digits.length > 15) return 'Phone number is too long';
    return '';
  },
  zipCode: (val) => {
    const v = (val || '').trim();
    if (!v) return 'Zip code is required';
    if (!/^\d{5}(-\d{4})?$/.test(v)) return 'Please enter a valid 5-digit zip code';
    return '';
  },
};

// Strip HTML tags and dangerous URLs from user input before sending to Airtable
const sanitize = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .slice(0, 2000);
};

// Format phone as user types: (555) 123-4567
const formatPhone = (val) => {
  const digits = (val || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Rate limit: 30 seconds between submissions per form
const RATE_LIMIT_MS = 30000;

// ============================================================================
// [API] - AIRTABLE FUNCTIONS
// ============================================================================

const AirtableAPI = {
  async getGurus() {
    try {
      const response = await fetch(AIRTABLE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getGurus',
          table: TABLES.gurus,
          query: 'filterByFormula={Status}="Active"&sort[0][field]=Display Order&sort[0][direction]=asc',
        }),
      });
      if (!response.ok) throw new Error('Failed to fetch gurus');
      const data = await response.json();
      if (!data.records) return [];
      return data.records.map(record => ({
        id: record.id,
        name: record.fields['Name'] || '',
        location: record.fields['Location'] || '',
        specialty: record.fields['Primary Specialty'] || '',
        tags: record.fields['Tags'] || [],
        tagline: record.fields['Tagline'] || '',
        bio: record.fields['Bio'] || '',
        photoUrl: record.fields['Photo URL'] || null,
        videoUrl: record.fields['Video URL'] || null,
      }));
    } catch (error) {
      console.error('Error fetching gurus:', error);
      return [];
    }
  },

  async submitInquiry(data) {
    try {
      // Honeypot - silently succeed so bots don't retry
      if (data.website && data.website.trim()) {
        return { success: true };
      }

      const fields = {
        'Name': sanitize(data.name),
        'Email': sanitize(data.email),
        'Phone': sanitize(data.phone),
        'Type': sanitize(data.type),
        'Message': sanitize(data.message),
        'Status': 'New',
      };

      if (data.targetGuruId) {
        fields['Target Guru'] = [data.targetGuruId];
      }

      const response = await fetch(AIRTABLE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submitInquiry',
          table: TABLES.inquiries,
          data: { records: [{ fields }] },
        }),
      });
      if (!response.ok) throw new Error('Failed to submit inquiry');
      return { success: true };
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      return { success: false };
    }
  },

  async submitApplication(data) {
    try {
      if (data.website && data.website.trim()) {
        return { success: true };
      }

      const response = await fetch(AIRTABLE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submitApplication',
          table: TABLES.applications,
          data: {
            records: [{
              fields: {
                'Name': sanitize(data.name),
                'Email': sanitize(data.email),
                'Location': sanitize(data.location),
                'Primary Specialty': sanitize(data.specialty),
                'Message': sanitize(data.message),
                'Status': 'Pending',
              }
            }]
          },
        }),
      });
      if (!response.ok) throw new Error('Failed to submit application');
      return { success: true };
    } catch (error) {
      console.error('Error submitting application:', error);
      return { success: false };
    }
  },

  async submitGivesApplication(data) {
    try {
      if (data.website && data.website.trim()) {
        return { success: true };
      }

      const fields = {
        'Name': sanitize(data.name),
        'Email': sanitize(data.email),
        'Zip Code': sanitize(data.zipCode),
        'Story': sanitize(data.story),
        'Status': 'New',
      };

      if (data.phone && data.phone.trim()) {
        fields['Phone'] = sanitize(data.phone);
      }

      const response = await fetch(AIRTABLE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submitGivesApplication',
          table: TABLES.givesApplications,
          data: { records: [{ fields }] },
        }),
      });
      if (!response.ok) throw new Error('Failed to submit gives application');
      return { success: true };
    } catch (error) {
      console.error('Error submitting gives application:', error);
      return { success: false };
    }
  },

  async submitDonationInterest(data) {
    try {
      if (data.website && data.website.trim()) {
        return { success: true };
      }

      const fields = {
        'Name': sanitize(data.name),
        'Email': sanitize(data.email),
        'Type': sanitize(data.type),
        'Message': sanitize(data.message),
        'Status': 'New',
      };

      if (data.phone && data.phone.trim()) {
        fields['Phone'] = sanitize(data.phone);
      }

      const response = await fetch(AIRTABLE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submitDonationInterest',
          table: TABLES.donationInterests,
          data: { records: [{ fields }] },
        }),
      });
      if (!response.ok) throw new Error('Failed to submit donation interest');
      return { success: true };
    } catch (error) {
      console.error('Error submitting donation interest:', error);
      return { success: false };
    }
  },
};

// ============================================================================
// [ROUTING]
// ============================================================================

const pathToPage = (path) => {
  const p = (path || '/').toLowerCase().replace(/\/$/, '') || '/';
  if (p === '/' || p === '') return 'home';
  if (p === '/gurus') return 'gurus';
  if (p === '/discover') return 'discover';
  if (p === '/give' || p === '/gives') return 'give';
  if (p === '/become') return 'become';
  if (p === '/connect') return 'connect';
  return 'home';
};

const pageToPath = (page) => {
  switch (page) {
    case 'home': return '/';
    case 'gurus': return '/gurus';
    case 'discover': return '/discover';
    case 'give': return '/give';
    case 'become': return '/become';
    case 'connect': return '/connect';
    default: return '/';
  }
};

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

export default function GardenGurusGrow() {
  const initialPage = typeof window !== 'undefined'
    ? pathToPage(window.location.pathname)
    : 'home';

  const [currentPage, setCurrentPageState] = useState(initialPage);
  const [selectedGuru, setSelectedGuru] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All locations');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [gurus, setGurus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const setCurrentPage = (page) => {
    setCurrentPageState(page);
    if (typeof window !== 'undefined') {
      const newPath = pageToPath(page);
      if (window.location.pathname !== newPath) {
        window.history.pushState({ page }, '', newPath);
      }
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPageState(pathToPage(window.location.pathname));
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    async function loadGurus() {
      setIsLoading(true);
      const data = await AirtableAPI.getGurus();
      setGurus(data);
      setIsLoading(false);
    }
    loadGurus();
  }, []);

  const cities = ['All locations', ...Array.from(new Set(gurus.map(g => g.location).filter(Boolean))).sort()];

  const filteredGurus = gurus.filter(guru => {
    const specialtyMatch = selectedSpecialty === 'All' ||
      guru.specialty === selectedSpecialty ||
      (guru.tags && guru.tags.includes(selectedSpecialty));
    const locationMatch = selectedLocation === 'All locations' || guru.location === selectedLocation;
    return specialtyMatch && locationMatch;
  });

  // ----- Logo -----
  const Logo = () => (
    <div
      className="logo"
      onClick={() => { setCurrentPage('home'); setMobileMenuOpen(false); }}
    >
      <img src={IMAGES.logo} alt="Garden Gurus Grow" />
    </div>
  );

  // ----- Navigation -----
  const Navigation = () => {
    const navItems = [
      { id: 'gurus', label: 'Gurus' },
      { id: 'discover', label: 'Discover' },
      { id: 'give', label: 'Give' },
      { id: 'become', label: 'Become' },
      { id: 'connect', label: 'Connect' },
    ];
    return (
      <nav className="nav">
        <Logo />
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>
        <div className="nav-links desktop-only">
          {navItems.map(item => (
            <span
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              className={`nav-link ${currentPage === item.id ? 'active' : ''}`}
            >
              {item.label}
            </span>
          ))}
        </div>
        {mobileMenuOpen && (
          <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
            <div className="mobile-nav" onClick={e => e.stopPropagation()}>
              <button className="mobile-nav-close" onClick={() => setMobileMenuOpen(false)}>×</button>
              {navItems.map(item => (
                <span
                  key={item.id}
                  onClick={() => { setCurrentPage(item.id); setMobileMenuOpen(false); }}
                  className={`mobile-nav-link ${currentPage === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </nav>
    );
  };

  // ----- Footer -----
  const Footer = () => (
    <footer className="footer">
      <p className="footer-mission">In partnership with Garden Gurus Give</p>
      <p className="footer-tagline">Anyone can grow food anywhere.</p>
    </footer>
  );

  // ----- Divider -----
  const Divider = () => (
    <div className="divider"><div className="divider-line" /></div>
  );

  // ============================================================================
  // [GURU MODAL] - Renders for any page when a guru is selected
  // ============================================================================

  const GuruModal = () => {
    const [modalForm, setModalForm] = useState({
      name: '', email: '', phone: '', message: '', website: ''
    });
    const [modalErrors, setModalErrors] = useState({});
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const [modalSubmitStatus, setModalSubmitStatus] = useState(null);
    const lastModalSubmit = useRef(0);
    const prevGuruId = useRef(null);

    if (selectedGuru && prevGuruId.current !== selectedGuru.id) {
      prevGuruId.current = selectedGuru.id;
      if (modalSubmitStatus !== null || Object.keys(modalErrors).length > 0) {
        setModalForm({ name: '', email: '', phone: '', message: '', website: '' });
        setModalErrors({});
        setModalSubmitStatus(null);
      }
    }

    if (!selectedGuru) return null;

    const validateModal = () => {
      const errs = {};
      errs.name = Validators.name(modalForm.name);
      errs.email = Validators.email(modalForm.email);
      errs.phone = Validators.phone(modalForm.phone);
      errs.message = Validators.message(modalForm.message, 10);
      Object.keys(errs).forEach(k => { if (!errs[k]) delete errs[k]; });
      setModalErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleModalSubmit = async (e) => {
      e.preventDefault();
      if (!validateModal()) return;

      const now = Date.now();
      if (now - lastModalSubmit.current < RATE_LIMIT_MS) {
        setModalSubmitStatus('rate-limited');
        return;
      }

      setModalSubmitting(true);
      const result = await AirtableAPI.submitInquiry({
        name: modalForm.name,
        email: modalForm.email,
        phone: modalForm.phone,
        message: modalForm.message,
        website: modalForm.website,
        type: `Contact Guru: ${selectedGuru.name}`,
        targetGuruId: selectedGuru.id,
      });

      if (result.success) {
        setModalSubmitStatus('success');
        lastModalSubmit.current = now;
        setModalForm({ name: '', email: '', phone: '', message: '', website: '' });
      } else {
        setModalSubmitStatus('error');
      }
      setModalSubmitting(false);
    };

    const firstName = selectedGuru.name.split(' ')[0];

    return (
      <div className="modal-overlay" onClick={() => setSelectedGuru(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setSelectedGuru(null)}>×</button>

          <div className="video-section">
            {selectedGuru.videoUrl ? (
              <iframe
                className="guru-video"
                src={selectedGuru.videoUrl}
                title={`Meet ${selectedGuru.name}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="video-placeholder">
                <svg viewBox="0 0 80 80" className="play-icon">
                  <circle cx="40" cy="40" r="36" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <path d="M32 28l24 12-24 12z" fill="currentColor"/>
                </svg>
                <p className="video-label">Meet {firstName}</p>
              </div>
            )}
          </div>

          <div className="modal-guru-info">
            <h2 className="modal-guru-name">{selectedGuru.name}</h2>
            <p className="modal-guru-tagline">{selectedGuru.tagline}</p>
            <p className="modal-guru-location">{selectedGuru.location} · {selectedGuru.specialty}</p>
          </div>
          <div className="guru-tags">
            {selectedGuru.tags.map(tag => (
              <span key={tag} className="guru-tag">{tag}</span>
            ))}
          </div>
          {selectedGuru.bio && (
            <p className="modal-guru-bio">{selectedGuru.bio}</p>
          )}

          <div className="services-overview">
            <h3 className="services-title">How {firstName} can help</h3>
            <div className="services-list">
              <div className="service-item">
                <h4>Get You Started</h4>
                <p>Site assessment, water access planning, and a custom planting blueprint</p>
              </div>
              <div className="service-item">
                <h4>Garden Maintenance</h4>
                <p>Recurring visits to tend, teach, and troubleshoot your growing garden</p>
              </div>
              <div className="service-item">
                <h4>Design and Build</h4>
                <p>Full custom garden design and professional installation</p>
              </div>
            </div>
          </div>

          <div className="modal-message-form">
            <h3 className="message-form-title">Send {firstName} a message</h3>
            <p className="message-form-subtitle">
              Tell them about your space and what you're hoping to grow. They'll be in touch directly.
            </p>

            {modalSubmitStatus === 'success' && (
              <div className="form-success-modal">
                Your message is on its way to {firstName}. They'll reach out soon.
              </div>
            )}
            {modalSubmitStatus === 'error' && (
              <div className="form-error">
                Something went wrong. Please try again or use the Connect page.
              </div>
            )}
            {modalSubmitStatus === 'rate-limited' && (
              <div className="form-error">
                Please wait a moment before sending another message.
              </div>
            )}

            {modalSubmitStatus !== 'success' && (
              <form onSubmit={handleModalSubmit} noValidate>
                <input
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  className="honeypot"
                  value={modalForm.website}
                  onChange={e => setModalForm({ ...modalForm, website: e.target.value })}
                  aria-hidden="true"
                />

                <div className="modal-form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={modalForm.name}
                      onChange={e => setModalForm({ ...modalForm, name: e.target.value })}
                      autoComplete="name"
                      maxLength="100"
                      className={modalErrors.name ? 'error' : ''}
                    />
                    {modalErrors.name && <span className="error-text">{modalErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email"
                      value={modalForm.email}
                      onChange={e => setModalForm({ ...modalForm, email: e.target.value })}
                      autoComplete="email"
                      maxLength="254"
                      className={modalErrors.email ? 'error' : ''}
                    />
                    {modalErrors.email && <span className="error-text">{modalErrors.email}</span>}
                  </div>
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={modalForm.phone}
                    onChange={e => setModalForm({ ...modalForm, phone: formatPhone(e.target.value) })}
                    autoComplete="tel"
                    className={modalErrors.phone ? 'error' : ''}
                  />
                  {modalErrors.phone && <span className="error-text">{modalErrors.phone}</span>}
                </div>
                <div className="form-group">
                  <textarea
                    placeholder={`Tell ${firstName} about your space, goals, and what you're hoping to grow...`}
                    rows={4}
                    value={modalForm.message}
                    onChange={e => setModalForm({ ...modalForm, message: e.target.value })}
                    maxLength="2000"
                    className={modalErrors.message ? 'error' : ''}
                  />
                  {modalErrors.message && <span className="error-text">{modalErrors.message}</span>}
                </div>
                <button type="submit" className="message-submit-btn" disabled={modalSubmitting}>
                  {modalSubmitting ? 'Sending...' : `Message ${firstName}`}
                </button>
                <p className="message-form-trust">
                  Your message goes directly to {firstName}. We'll never share your info.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // [HOME] - FIND A GURU
  // ============================================================================

  const HomePage = () => {

    return (
      <div>
        <section className="hero" style={{ backgroundImage: `url('${IMAGES.heroBg}')` }}>
          <div className="hero-overlay">
            <h1 className="hero-title">There's organic. And then there's homegrown.</h1>
            <p className="hero-subtitle">
              Connect with a local guru to grow your own food at home.
            </p>
          </div>
        </section>

        <Divider />

        {/* Location + Specialty Filters */}
        <section className="filters">
          <div className="location-filter">
            <select
              value={selectedLocation}
              onChange={e => setSelectedLocation(e.target.value)}
              className="location-select"
            >
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-scroll">
            {SPECIALTIES.map(s => (
              <button
                key={s}
                onClick={() => setSelectedSpecialty(s)}
                className={`filter-pill ${selectedSpecialty === s ? 'active' : ''}`}
              >
                {s}
              </button>
            ))}
          </div>
        </section>

        {/* Guru Gallery */}
        <section className="gallery">
          {isLoading ? (
            <div className="loading">Loading Garden Gurus...</div>
          ) : (
            <div className="guru-grid">
              {filteredGurus.slice(0, 6).map(guru => (
                <div
                  key={guru.id}
                  onClick={() => setSelectedGuru(guru)}
                  className="guru-card"
                >
                  <div className="guru-photo">
                    {guru.photoUrl ? (
                      <img src={guru.photoUrl} alt={guru.name} />
                    ) : (
                      <svg viewBox="0 0 80 80" className="guru-placeholder-icon">
                        <circle cx="40" cy="28" r="16" fill="currentColor"/>
                        <path d="M12 72c0-20 12-28 28-28s28 8 28 28" fill="currentColor"/>
                      </svg>
                    )}
                  </div>
                  <div className="guru-info">
                    <h3 className="guru-name">{guru.name}</h3>
                    <p className="guru-specialty">{guru.tags.join(', ')}</p>
                    <p className="guru-location">{guru.location}</p>
                  </div>
                </div>
              ))}

            </div>
          )}

          <div className="browse-all-cta">
            <button onClick={() => setCurrentPage('gurus')} className="browse-all-btn">
              Browse All Gurus
            </button>
          </div>
        </section>


        {/* Impact Section */}
        <section className="impact">
          <Divider />
          <p className="impact-eyebrow">Our Commitment</p>
          <h2 className="impact-title">Every connection grows the giveback</h2>

          <div className="impact-cards">
            <div className="impact-card">
              <h3 className="impact-card-title grow">Garden Gurus Grow</h3>
              <p className="impact-card-text">Connect with skilled local growers who teach, guide, and tend gardens alongside you.</p>
            </div>

            <div className="impact-card">
              <h3 className="impact-card-title gives">Garden Gurus Give</h3>
              <p className="impact-card-text">Our nonprofit partner brings gardens to families who need them most.</p>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // ============================================================================
  // [DISCOVER]
  // ============================================================================

  const DiscoverPage = () => (
    <div>
      <section className="page-hero">
        <p className="eyebrow">Our Mission</p>
        <h1 className="page-title">Anyone can grow food anywhere</h1>
        <p className="page-subtitle">
          We connect families with local garden experts who teach, guide, and grow alongside them.
        </p>
      </section>

      <Divider />

      <section className="story">
        <div className="story-grid">
          <div className="story-image">
            {IMAGES.crystalPhoto ? (
              <img src={IMAGES.crystalPhoto} alt="Crystal, Founder of Garden Gurus" />
            ) : (
              <span>PHOTO OF CRYSTAL</span>
            )}
          </div>
          <div className="story-content">
            <h2 className="story-title">Why we exist</h2>
            <p>
              Anyone can grow food anywhere. Most of us were just never shown how.
            </p>
            <p>
              Garden Gurus connects you with experienced growers in your neighborhood. People who know the soil, the seasons, and the small things that make a garden thrive. People who've spent years learning, and love nothing more than passing it on.
            </p>
            <p>
              When you grow your own food, something shifts. Meals taste different. Your kids see where food comes from. You meet your neighbors, and pretty soon you're trading tomatoes, swapping seeds, building the kind of community that used to grow on every block. You realize you can do this. And you're not doing it alone.
            </p>
            <p>
              And if you want a garden but can't afford one, you're still welcome here. Through Garden Gurus Give, our nonprofit, we raise money to build gardens for families who need them. Because growing food shouldn't be a privilege.
            </p>
            <p>
              That's what we're here for.
            </p>
            <p className="signature">
              Crystal<br/>
              <span>Founder</span>
            </p>
          </div>
        </div>
      </section>

      <section className="beliefs">
        <h2 className="section-title">What we believe</h2>
        <div className="beliefs-grid">
          <div className="belief">
            <h3>Food is Freedom</h3>
            <p>When you can grow your own food, you're not dependent on supply chains, store hours, or rising prices.</p>
          </div>
          <div className="belief">
            <h3>Knowledge Over Transaction</h3>
            <p>We don't just install gardens, we teach you to grow. The knowledge stays with you forever.</p>
          </div>
          <div className="belief">
            <h3>Community Over Competition</h3>
            <p>Our Garden Gurus share knowledge, trade plants, and support each other. When one grows, we all grow.</p>
          </div>
        </div>
      </section>

      <section className="giveback">
        <h2>Garden Gurus Give</h2>
        <p>
          Our nonprofit partner brings gardens to families who need them most. When you support
          our community of growers, you help fund garden installations for those facing food insecurity.
        </p>
        <button onClick={() => setCurrentPage('give')} className="giveback-btn">
          Learn More
        </button>
      </section>
    </div>
  );

  // ============================================================================
  // [GURUS] - FULL DIRECTORY PAGE
  // ============================================================================

  const GurusPage = () => (
    <div>
      <section className="page-hero">
        <p className="eyebrow">Meet the Network</p>
        <h1 className="page-title">All Garden Gurus</h1>
        <p className="page-subtitle">
          Browse our growing network of local growers, ready to help you cultivate your space.
        </p>
      </section>

      <section className="filters directory-filters">
        <div className="location-filter">
          <select
            value={selectedLocation}
            onChange={e => setSelectedLocation(e.target.value)}
            className="location-select"
          >
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-scroll">
          {SPECIALTIES.map(s => (
            <button
              key={s}
              onClick={() => setSelectedSpecialty(s)}
              className={`filter-pill ${selectedSpecialty === s ? 'active' : ''}`}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      <section className="directory-gallery">
        {isLoading ? (
          <div className="loading">Loading Garden Gurus...</div>
        ) : filteredGurus.length === 0 ? (
          <div className="loading">No gurus match this filter yet. Try another specialty.</div>
        ) : (
          <>
            <div className="directory-grid">
              {filteredGurus.map(guru => (
                <div
                  key={guru.id}
                  onClick={() => setSelectedGuru(guru)}
                  className="guru-card-grid"
                >
                  <div className="guru-photo-grid">
                    {guru.photoUrl ? (
                      <img src={guru.photoUrl} alt={guru.name} />
                    ) : (
                      <svg viewBox="0 0 80 80" className="guru-placeholder-icon">
                        <circle cx="40" cy="28" r="16" fill="currentColor"/>
                        <path d="M12 72c0-20 12-28 28-28s28 8 28 28" fill="currentColor"/>
                      </svg>
                    )}
                    {guru.specialty && (
                      <div className="guru-specialty-pill">{guru.specialty}</div>
                    )}
                  </div>
                  <div className="guru-info-grid">
                    <h3 className="guru-name-grid">{guru.name}</h3>
                    <p className="guru-location-grid">{guru.location}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="directory-count">
              Showing {filteredGurus.length} {filteredGurus.length === 1 ? 'guru' : 'gurus'}
            </p>
          </>
        )}
      </section>
    </div>
  );

  // ============================================================================
  // [GIVE] - Garden Gurus Give: Mission + Apply for a Garden + Donate
  // ============================================================================

  const GivePage = () => {
    const [appData, setAppData] = useState({
      name: '', email: '', phone: '', zipCode: '', story: '', website: ''
    });
    const [appErrors, setAppErrors] = useState({});
    const [appSubmitting, setAppSubmitting] = useState(false);
    const [appStatus, setAppStatus] = useState(null);
    const lastAppSubmit = useRef(0);

    const [donateData, setDonateData] = useState({
      name: '', email: '', phone: '', type: '', message: '', website: ''
    });
    const [donateErrors, setDonateErrors] = useState({});
    const [donateSubmitting, setDonateSubmitting] = useState(false);
    const [donateStatus, setDonateStatus] = useState(null);
    const lastDonateSubmit = useRef(0);

    const validateApp = () => {
      const errs = {};
      errs.name = Validators.name(appData.name);
      errs.email = Validators.email(appData.email);
      errs.zipCode = Validators.zipCode(appData.zipCode);
      errs.story = Validators.message(appData.story, 30);
      errs.phone = Validators.phoneOptional(appData.phone);
      Object.keys(errs).forEach(k => { if (!errs[k]) delete errs[k]; });
      setAppErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleAppSubmit = async (e) => {
      e.preventDefault();
      if (!validateApp()) return;

      const now = Date.now();
      if (now - lastAppSubmit.current < RATE_LIMIT_MS) {
        setAppStatus('rate-limited');
        return;
      }

      setAppSubmitting(true);
      const result = await AirtableAPI.submitGivesApplication(appData);

      if (result.success) {
        setAppStatus('success');
        lastAppSubmit.current = now;
        setAppData({ name: '', email: '', phone: '', zipCode: '', story: '', website: '' });
      } else {
        setAppStatus('error');
      }
      setAppSubmitting(false);
    };

    const validateDonate = () => {
      const errs = {};
      errs.name = Validators.name(donateData.name);
      errs.email = Validators.email(donateData.email);
      errs.type = Validators.selection(donateData.type, 'Please tell us how you want to support');
      errs.phone = Validators.phoneOptional(donateData.phone);
      Object.keys(errs).forEach(k => { if (!errs[k]) delete errs[k]; });
      setDonateErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleDonateSubmit = async (e) => {
      e.preventDefault();
      if (!validateDonate()) return;

      const now = Date.now();
      if (now - lastDonateSubmit.current < RATE_LIMIT_MS) {
        setDonateStatus('rate-limited');
        return;
      }

      setDonateSubmitting(true);
      const result = await AirtableAPI.submitDonationInterest(donateData);

      if (result.success) {
        setDonateStatus('success');
        lastDonateSubmit.current = now;
        setDonateData({ name: '', email: '', phone: '', type: '', message: '', website: '' });
      } else {
        setDonateStatus('error');
      }
      setDonateSubmitting(false);
    };

    return (
      <div>
        <section className="page-hero">
          <p className="eyebrow">Our Nonprofit</p>
          <h1 className="page-title">Garden Gurus Give</h1>
          <p className="page-subtitle">
            Building food security in underserved communities, one garden at a time.
          </p>
        </section>

        <Divider />

        {/* MISSION */}
        <section className="give-mission">
          <div className="give-mission-content">
            <h2 className="section-title">Our Mission</h2>
            <p>
              Garden Gurus Give is a 501(c)(3) nonprofit organization. We believe everyone deserves access to fresh, nutritious food, regardless of their circumstances.
            </p>
            <p>
              Through partnerships with local Garden Gurus and community organizations, we design, build, and maintain gardens for underserved families at no cost to them. Each Gift Garden includes plants, supplies, and hands-on guidance from a Garden Guru to help families cultivate their own sustainable food source.
            </p>
          </div>

          <div className="gives-steps">
            <div className="gives-step">
              <div className="gives-step-num">1</div>
              <h4>Community Identifies Need</h4>
              <p>We partner with schools, churches, and community organizations to find families who would benefit most.</p>
            </div>
            <div className="gives-step">
              <div className="gives-step-num">2</div>
              <h4>Gurus Build Gardens</h4>
              <p>Our Garden Gurus design and install gardens, then teach recipients how to tend and grow.</p>
            </div>
            <div className="gives-step">
              <div className="gives-step-num">3</div>
              <h4>Families Grow</h4>
              <p>Families gain food, knowledge, and the lasting independence that comes with growing their own.</p>
            </div>
          </div>
        </section>

        {/* APPLY FOR A GARDEN */}
        <section className="give-application">
          <div className="give-application-inner">
            <p className="eyebrow green">Apply for a Gift Garden</p>
            <h2 className="section-title">Want to grow food at home?</h2>
            <p className="give-application-intro">
              We believe everyone deserves access to fresh, nutritious food and the empowerment of growing it. Through the generosity of our community, we're donating Gift Gardens worth over $1,000 to households or communities ready to grow. Each garden includes plants, supplies, and hands-on guidance from a Garden Guru.
            </p>

            {appStatus === 'success' && (
              <div className="form-success contact-success">
                Thank you for sharing your story and vision with us. We are grateful for your interest in growing with Garden Gurus Give. Selected recipients will be notified by June 1, 2026 via email.
              </div>
            )}
            {appStatus === 'error' && (
              <div className="form-error">Something went wrong. Please try again.</div>
            )}
            {appStatus === 'rate-limited' && (
              <div className="form-error">Please wait a moment before submitting again.</div>
            )}

            {appStatus !== 'success' && (
              <form className="give-form" onSubmit={handleAppSubmit} noValidate>
                <input
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  className="honeypot"
                  value={appData.website}
                  onChange={e => setAppData({ ...appData, website: e.target.value })}
                  aria-hidden="true"
                />

                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Name"
                      value={appData.name}
                      onChange={e => setAppData({ ...appData, name: e.target.value })}
                      autoComplete="name"
                      maxLength="100"
                      className={appErrors.name ? 'error' : ''}
                    />
                    {appErrors.name && <span className="error-text">{appErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email"
                      value={appData.email}
                      onChange={e => setAppData({ ...appData, email: e.target.value })}
                      autoComplete="email"
                      maxLength="254"
                      className={appErrors.email ? 'error' : ''}
                    />
                    {appErrors.email && <span className="error-text">{appErrors.email}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={appData.phone}
                      onChange={e => setAppData({ ...appData, phone: formatPhone(e.target.value) })}
                      autoComplete="tel"
                      className={appErrors.phone ? 'error' : ''}
                    />
                    {appErrors.phone && <span className="error-text">{appErrors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Zip Code"
                      value={appData.zipCode}
                      onChange={e => setAppData({ ...appData, zipCode: e.target.value })}
                      autoComplete="postal-code"
                      maxLength="10"
                      className={appErrors.zipCode ? 'error' : ''}
                    />
                    {appErrors.zipCode && <span className="error-text">{appErrors.zipCode}</span>}
                  </div>
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="In a few sentences, tell us how this garden would contribute to your household or community. How do you plan to sustain it and share what you learn with others?"
                    rows={6}
                    value={appData.story}
                    onChange={e => setAppData({ ...appData, story: e.target.value })}
                    maxLength="2000"
                    className={appErrors.story ? 'error' : ''}
                  />
                  {appErrors.story && <span className="error-text">{appErrors.story}</span>}
                </div>
                <button type="submit" className="form-submit" disabled={appSubmitting}>
                  {appSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
                <p className="form-trust" style={{color: 'rgba(250,248,245,0.85)'}}>Selected recipients will be notified by June 1, 2026.</p>
              </form>
            )}
          </div>
        </section>

        {/* DONATE */}
        <section className="give-donate">
          <div className="give-donate-inner">
            <p className="eyebrow">Support The Mission</p>
            <h2 className="give-donate-title">Help us grow more gardens</h2>
            <p className="give-donate-text">
              Your support funds garden installations for families who can't afford them. Whether you'd like to make a one-time donation, set up monthly giving, or sponsor a specific garden, we'd love to hear from you.
            </p>

            {donateStatus === 'success' && (
              <div className="form-success donate-success">
                Thank you for your interest in supporting our mission. We'll be in touch soon with details on how to contribute.
              </div>
            )}
            {donateStatus === 'error' && (
              <div className="form-error">Something went wrong. Please try again.</div>
            )}
            {donateStatus === 'rate-limited' && (
              <div className="form-error">Please wait a moment before submitting again.</div>
            )}

            {donateStatus !== 'success' && (
              <form className="donate-form" onSubmit={handleDonateSubmit} noValidate>
                <input
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  className="honeypot"
                  value={donateData.website}
                  onChange={e => setDonateData({ ...donateData, website: e.target.value })}
                  aria-hidden="true"
                />

                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Name"
                      value={donateData.name}
                      onChange={e => setDonateData({ ...donateData, name: e.target.value })}
                      autoComplete="name"
                      maxLength="100"
                      className={donateErrors.name ? 'error' : ''}
                    />
                    {donateErrors.name && <span className="error-text">{donateErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email"
                      value={donateData.email}
                      onChange={e => setDonateData({ ...donateData, email: e.target.value })}
                      autoComplete="email"
                      maxLength="254"
                      className={donateErrors.email ? 'error' : ''}
                    />
                    {donateErrors.email && <span className="error-text">{donateErrors.email}</span>}
                  </div>
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={donateData.phone}
                    onChange={e => setDonateData({ ...donateData, phone: formatPhone(e.target.value) })}
                    autoComplete="tel"
                    className={donateErrors.phone ? 'error' : ''}
                  />
                  {donateErrors.phone && <span className="error-text">{donateErrors.phone}</span>}
                </div>
                <div className="form-group">
                  <select
                    value={donateData.type}
                    onChange={e => setDonateData({ ...donateData, type: e.target.value })}
                    className={donateErrors.type ? 'error' : ''}
                  >
                    <option value="" disabled>How would you like to support?</option>
                    <option value="One-time donation">I want to make a one-time donation</option>
                    <option value="Monthly giving">I want to set up monthly giving</option>
                    <option value="Sponsor a garden">I want to sponsor a specific garden</option>
                    <option value="Other">Other</option>
                  </select>
                  {donateErrors.type && <span className="error-text">{donateErrors.type}</span>}
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="Anything else we should know? (optional)"
                    rows={3}
                    value={donateData.message}
                    onChange={e => setDonateData({ ...donateData, message: e.target.value })}
                    maxLength="2000"
                  />
                </div>
                <button type="submit" className="donate-submit-btn" disabled={donateSubmitting}>
                  {donateSubmitting ? 'Sending...' : 'Request More Info'}
                </button>
                <p className="form-trust">We'll get back to you within a few days with details.</p>
              </form>
            )}
          </div>
        </section>
      </div>
    );
  };

  // ============================================================================
  // [BECOME] - APPLY TO BECOME A GURU
  // ============================================================================

  const BecomePage = () => {
    const [formData, setFormData] = useState({
      name: '', email: '', location: '', specialty: '', message: '', website: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const lastSubmit = useRef(0);

    const validateForm = () => {
      const errs = {};
      errs.name = Validators.name(formData.name);
      errs.email = Validators.email(formData.email);
      errs.location = Validators.location(formData.location);
      errs.specialty = Validators.selection(formData.specialty, 'Please select your specialty');
      errs.message = Validators.message(formData.message, 20);
      Object.keys(errs).forEach(k => { if (!errs[k]) delete errs[k]; });
      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      const now = Date.now();
      if (now - lastSubmit.current < RATE_LIMIT_MS) {
        setSubmitStatus('rate-limited');
        return;
      }

      setIsSubmitting(true);
      const result = await AirtableAPI.submitApplication(formData);

      if (result.success) {
        setSubmitStatus('success');
        lastSubmit.current = now;
        setFormData({ name: '', email: '', location: '', specialty: '', message: '', website: '' });
      } else {
        setSubmitStatus('error');
      }
      setIsSubmitting(false);
    };

    return (
      <div>
        <section className="page-hero">
          <p className="eyebrow green">Join Our Network</p>
          <h1 className="page-title">Become a Garden Guru</h1>
          <p className="page-subtitle">
            Share your growing expertise and get paid for what you know.
          </p>
        </section>

        <section className="benefits">
          <div className="benefits-grid">
            <div className="benefit">
              <h3>Keep 100%</h3>
              <p>Set your own rates. Work directly with clients. We don't take a cut.</p>
            </div>
            <div className="benefit">
              <h3>Your schedule</h3>
              <p>Accept clients when it works for you. Full flexibility, no minimums.</p>
            </div>
            <div className="benefit">
              <h3>Real impact</h3>
              <p>Be part of a community building food security through Garden Gurus Give.</p>
            </div>
          </div>
        </section>

        <Divider />

        <section className="earnings">
          <h2 className="section-title">What gurus typically charge</h2>
          <p className="earnings-subtitle">Set your own rates. Here's what's common in the field:</p>
          <div className="earnings-grid">
            <div className="earning">
              <p className="earning-service">Get Started Session</p>
              <p className="earning-range">$95 to $150</p>
              <p className="earning-note">90-min consultation</p>
            </div>
            <div className="earning">
              <p className="earning-service">Maintenance Visits</p>
              <p className="earning-range">$75 to $170</p>
              <p className="earning-note">Per visit, varies by frequency</p>
            </div>
            <div className="earning">
              <p className="earning-service">Design and Build</p>
              <p className="earning-range">$3,750 to $6,000+</p>
              <p className="earning-note">Complete transformations</p>
            </div>
          </div>
        </section>

        <section className="apply-form">
          <h2>Apply to become a Garden Guru</h2>
          <p>Tell us about yourself. We'll be in touch within a few days.</p>

          {submitStatus === 'success' && (
            <div className="form-success">
              Thanks for applying! We'll be in touch soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="form-error">
              Something went wrong. Please try again.
            </div>
          )}
          {submitStatus === 'rate-limited' && (
            <div className="form-error">
              Please wait a moment before submitting again.
            </div>
          )}

          {submitStatus !== 'success' && (
            <form className="form" onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                className="honeypot"
                value={formData.website}
                onChange={e => setFormData({ ...formData, website: e.target.value })}
                aria-hidden="true"
              />

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    autoComplete="name"
                    maxLength="100"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    autoComplete="email"
                    maxLength="254"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="City, State"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    autoComplete="address-level2"
                    maxLength="100"
                    className={errors.location ? 'error' : ''}
                  />
                  {errors.location && <span className="error-text">{errors.location}</span>}
                </div>
                <div className="form-group">
                  <select
                    value={formData.specialty}
                    onChange={e => setFormData({ ...formData, specialty: e.target.value })}
                    className={errors.specialty ? 'error' : ''}
                  >
                    <option value="" disabled>Primary Specialty...</option>
                    {APPLICATION_SPECIALTIES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {errors.specialty && <span className="error-text">{errors.specialty}</span>}
                </div>
              </div>
              <div className="form-group">
                <textarea
                  placeholder="Why do you want to become a Garden Guru? Tell us about your experience and what you'd love to share."
                  rows={5}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  maxLength="2000"
                  className={errors.message ? 'error' : ''}
                />
                {errors.message && <span className="error-text">{errors.message}</span>}
              </div>
              <button type="submit" className="form-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          )}
        </section>
      </div>
    );
  };

  // ============================================================================
  // [CONNECT] - GENERAL CONTACT FORM
  // ============================================================================

  const ConnectPage = () => {
    const [formData, setFormData] = useState({
      name: '', email: '', phone: '', type: '', message: '', website: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const lastSubmit = useRef(0);

    const validateForm = () => {
      const errs = {};
      errs.name = Validators.name(formData.name);
      errs.email = Validators.email(formData.email);
      errs.phone = Validators.phoneOptional(formData.phone);
      errs.type = Validators.selection(formData.type, 'Please tell us what you need');
      errs.message = Validators.message(formData.message, 10);
      Object.keys(errs).forEach(k => { if (!errs[k]) delete errs[k]; });
      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      const now = Date.now();
      if (now - lastSubmit.current < RATE_LIMIT_MS) {
        setSubmitStatus('rate-limited');
        return;
      }

      setIsSubmitting(true);
      const result = await AirtableAPI.submitInquiry(formData);

      if (result.success) {
        setSubmitStatus('success');
        lastSubmit.current = now;
        setFormData({ name: '', email: '', phone: '', type: '', message: '', website: '' });
      } else {
        setSubmitStatus('error');
      }
      setIsSubmitting(false);
    };

    return (
      <div>
        <section className="page-hero connect-hero">
          <p className="eyebrow green">Get in Touch</p>
          <h1 className="page-title">Let's connect</h1>
          <p className="page-subtitle">
            Collaborations, press, partnerships, and everything in between.
          </p>
        </section>

        <section className="contact-form-section">
          <h2 className="section-title">Send us a message</h2>

          {submitStatus === 'success' && (
            <div className="form-success contact-success">
              Thanks for reaching out! We'll be in touch soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="form-error">
              Something went wrong. Please try again.
            </div>
          )}
          {submitStatus === 'rate-limited' && (
            <div className="form-error">
              Please wait a moment before submitting again.
            </div>
          )}

          {submitStatus !== 'success' && (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                className="honeypot"
                value={formData.website}
                onChange={e => setFormData({ ...formData, website: e.target.value })}
                aria-hidden="true"
              />

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    autoComplete="name"
                    maxLength="100"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    autoComplete="email"
                    maxLength="254"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  autoComplete="tel"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              <div className="form-group">
                <select
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="" disabled>What can we help with?</option>
                  <option value="Collaborate or partner">I'd like to collaborate or partner</option>
                  <option value="Press or media">Press, media, or speaking inquiry</option>
                  <option value="Tech issue">I have a tech issue with the website</option>
                  <option value="Something else">Something else</option>
                </select>
                {errors.type && <span className="error-text">{errors.type}</span>}
              </div>
              <div className="form-group">
                <textarea
                  placeholder="Tell us what's on your mind..."
                  rows={4}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  maxLength="2000"
                  className={errors.message ? 'error' : ''}
                />
                {errors.message && <span className="error-text">{errors.message}</span>}
              </div>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              <p className="form-trust">We'll respond within a few days.</p>
            </form>
          )}
        </section>
      </div>
    );
  };

  // ============================================================================
  // [STYLES] - ALL CSS
  // ============================================================================

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Karla:wght@300;400;500&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    .app {
      --sage: ${COLORS.sage};
      --terracotta: ${COLORS.terracotta};
      --cream: ${COLORS.cream};
      --charcoal: ${COLORS.charcoal};
      --warm-brown: ${COLORS.warmBrown};
      --light-sage: ${COLORS.lightSage};
      --light-brown: ${COLORS.lightBrown};

      min-height: 100vh;
      background-color: var(--cream);
      font-family: 'Karla', sans-serif;
      color: var(--charcoal);
      font-size: 16px;
      line-height: 1.6;
    }

    /* ========== HONEYPOT (hidden spam field) ========== */
    .honeypot {
      position: absolute !important;
      left: -10000px !important;
      top: auto !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    /* ========== NAVIGATION ========== */
    .nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      background: var(--cream);
      border-bottom: 1px solid var(--light-sage);
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .logo { cursor: pointer; }
    .logo img { height: 36px; }
    .mobile-menu-btn {
      display: flex;
      flex-direction: column;
      gap: 5px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
    }
    .mobile-menu-btn span {
      width: 24px;
      height: 2px;
      background: var(--charcoal);
      transition: all 0.3s;
    }
    .desktop-only { display: none; }
    .mobile-nav-overlay {
      position: fixed;
      inset: 0;
      background: rgba(44, 44, 44, 0.5);
      z-index: 200;
    }
    .mobile-nav {
      position: absolute;
      top: 0;
      right: 0;
      width: 280px;
      height: 100%;
      background: var(--cream);
      padding: 80px 30px 30px;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .mobile-nav-close {
      position: absolute;
      top: 16px;
      right: 20px;
      background: none;
      border: none;
      font-size: 2rem;
      cursor: pointer;
      color: #666;
    }
    .mobile-nav-link {
      font-size: 1.25rem;
      color: #666;
      cursor: pointer;
    }
    .mobile-nav-link.active { color: var(--charcoal); font-weight: 500; }

    /* ========== HOME HERO ========== */
    .hero {
      padding: 120px 20px;
      text-align: center;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      position: relative;
    }
    .hero-overlay {
      position: relative;
      z-index: 1;
    }
    .hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse at center, rgba(250, 248, 245, 0.7) 0%, rgba(250, 248, 245, 0.4) 100%);
      z-index: 0;
    }
    .hero-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 2.2rem;
      font-weight: 500;
      margin-bottom: 18px;
      line-height: 1.2;
      color: #2C2C2C;
      text-shadow: 0 1px 2px rgba(255,255,255,0.8);
    }
    .hero-subtitle {
      font-size: 1.05rem;
      color: #444;
      line-height: 1.65;
      max-width: 600px;
      margin: 0 auto;
      text-shadow: 0 1px 2px rgba(255,255,255,0.8);
    }

    /* ========== PAGE HERO (Discover/Gives/Become/Connect) ========== */
    /* THIS WAS BROKEN IN V1 - now has proper base mobile styles */
    .page-hero {
      padding: 70px 20px 50px;
      text-align: center;
      position: relative;
      overflow: hidden;
    }
    .page-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 2.2rem;
      font-weight: 500;
      line-height: 1.2;
      margin-bottom: 18px;
      color: var(--charcoal);
    }
    .page-subtitle {
      font-size: 1.05rem;
      color: #555;
      line-height: 1.65;
      max-width: 600px;
      margin: 0 auto;
    }

    .eyebrow {
      font-size: 0.8rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--terracotta);
      margin-bottom: 14px;
    }
    .eyebrow.green { color: var(--sage); }

    /* ========== DIVIDER ========== */
    .divider { padding: 32px 0; display: flex; justify-content: center; }
    .divider-line { width: 60px; height: 1px; background: var(--light-sage); }

    /* ========== FILTERS ========== */
    .filters { padding: 0 20px 20px; }
    .location-filter {
      padding: 0 0 14px;
      display: flex;
      justify-content: center;
    }
    .location-select {
      font-family: 'Karla', sans-serif;
      font-size: 0.95rem;
      padding: 10px 36px 10px 18px;
      border: 1px solid var(--light-sage);
      border-radius: 24px;
      background-color: var(--cream);
      color: var(--charcoal);
      cursor: pointer;
      min-width: 220px;
      appearance: none;
      -webkit-appearance: none;
      background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%2212%22 height%3D%2212%22 viewBox%3D%220 0 24 24%22 fill%3D%22none%22 stroke%3D%22%237C9070%22 stroke-width%3D%222%22%3E%3Cpolyline points%3D%226 9 12 15 18 9%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E");
      background-repeat: no-repeat;
      background-position: right 16px center;
    }
    .location-select:focus {
      outline: none;
      border-color: var(--sage);
    }
    .filter-scroll {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 8px;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .filter-scroll::-webkit-scrollbar { display: none; }
    .filter-pill {
      padding: 10px 16px;
      font-size: 0.9rem;
      border: 1px solid var(--light-sage);
      border-radius: 24px;
      background: transparent;
      color: #666;
      cursor: pointer;
      white-space: nowrap;
      flex-shrink: 0;
      font-family: inherit;
      transition: all 0.2s;
    }
    .filter-pill.active {
      background: var(--sage);
      border-color: var(--sage);
      color: var(--cream);
    }

    /* ========== GURU GALLERY ========== */
    .gallery { padding: 0 0 40px; }
    .loading { text-align: center; padding: 40px 20px; color: #666; font-size: 1rem; }
    .guru-grid {
      display: flex;
      gap: 16px;
      overflow-x: auto;
      padding: 0 20px 16px 20px;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .guru-grid::-webkit-scrollbar { display: none; }
    .guru-card {
      cursor: pointer;
      flex: 0 0 calc(100vw - 60px);
      scroll-snap-align: start;
    }
    .guru-photo {
      aspect-ratio: 9/16;
      background: #E8E4DF;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 14px;
      transition: transform 0.2s;
      overflow: hidden;
    }
    .guru-photo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .guru-card:active .guru-photo { transform: scale(0.98); }
    .guru-placeholder-icon {
      width: 60px;
      height: 60px;
      color: var(--warm-brown);
      opacity: 0.4;
    }
    .guru-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.4rem;
      font-weight: 500;
      margin-bottom: 6px;
    }
    .guru-specialty {
      font-size: 0.95rem;
      color: var(--sage);
      margin-bottom: 4px;
    }
    .guru-location {
      font-size: 0.85rem;
      color: var(--warm-brown);
    }

    .coming-soon-card { cursor: default; }
    .coming-soon-photo {
      flex-direction: column;
      padding: 30px 20px;
      text-align: center;
      background: linear-gradient(135deg, #f5f3f0 0%, #e8e4df 100%);
    }
    .coming-soon-icon { font-size: 2.5rem; margin-bottom: 16px; }
    .coming-soon-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.35rem;
      color: var(--charcoal);
      margin-bottom: 8px;
    }
    .coming-soon-text {
      font-size: 0.95rem;
      color: #666;
      margin-bottom: 16px;
    }
    .coming-soon-cta {
      font-size: 0.9rem;
      color: var(--sage);
      margin-bottom: 12px;
    }
    .coming-soon-btn {
      padding: 11px 22px;
      background: var(--terracotta);
      color: var(--cream);
      border: none;
      border-radius: 20px;
      font-family: inherit;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background 0.2s;
    }
    .coming-soon-btn:hover { background: #b8876a; }

    /* ========== MODAL ========== */
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(44, 44, 44, 0.85);
      z-index: 1000;
      overflow-y: auto;
    }
    .modal {
      background: var(--cream);
      min-height: 100vh;
      padding: 20px;
      padding-top: 60px;
    }
    .modal-close {
      position: fixed;
      top: 12px;
      right: 12px;
      background: var(--cream);
      border: none;
      font-size: 1.8rem;
      color: #666;
      cursor: pointer;
      z-index: 1001;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .video-section {
      margin-bottom: 24px;
      display: flex;
      justify-content: center;
    }
    .video-placeholder {
      aspect-ratio: 16/9;
      width: 100%;
      background: #E8E4DF;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }
    .guru-video {
      width: 100%;
      height: 280px;
      border-radius: 8px;
      border: none;
    }
    .play-icon { width: 50px; height: 50px; color: var(--sage); }
    .video-label { font-size: 0.9rem; color: var(--warm-brown); }
    .modal-guru-info { text-align: center; margin-bottom: 16px; }
    .modal-guru-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.7rem;
      margin-bottom: 8px;
    }
    .modal-guru-tagline {
      font-size: 1rem;
      color: #666;
      font-style: italic;
      margin-bottom: 8px;
    }
    .modal-guru-location {
      font-size: 0.9rem;
      color: var(--warm-brown);
      margin-bottom: 16px;
    }
    .guru-tags {
      display: flex;
      gap: 8px;
      justify-content: center;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
    .guru-tag {
      padding: 5px 13px;
      font-size: 0.75rem;
      background: var(--light-sage);
      color: var(--sage);
      border-radius: 12px;
    }
    .modal-guru-bio {
      font-size: 1rem;
      color: #555;
      line-height: 1.7;
      margin-bottom: 32px;
      text-align: center;
    }

    .services-overview { margin-bottom: 32px; }
    .services-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.4rem;
      text-align: center;
      margin-bottom: 20px;
    }
    .services-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .service-item {
      padding: 20px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid var(--light-brown);
    }
    .service-item h4 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.15rem;
      color: var(--sage);
      margin-bottom: 8px;
    }
    .service-item p {
      font-size: 0.95rem;
      color: #666;
      line-height: 1.55;
    }

    /* ========== MODAL CONTACT FORM ========== */
    .modal-message-form {
      background: var(--charcoal);
      border-radius: 8px;
      padding: 32px 24px;
    }
    .message-form-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.4rem;
      color: var(--cream);
      text-align: center;
      margin-bottom: 10px;
    }
    .message-form-subtitle {
      font-size: 0.95rem;
      color: rgba(250, 248, 245, 0.8);
      text-align: center;
      margin-bottom: 24px;
      line-height: 1.55;
    }
    .modal-form-row {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-bottom: 14px;
    }
    .modal-message-form .form-group { margin-bottom: 14px; }
    .modal-message-form input,
    .modal-message-form textarea {
      width: 100%;
      padding: 14px 16px;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-family: inherit;
      background: var(--cream);
      color: var(--charcoal);
    }
    .modal-message-form textarea {
      min-height: 110px;
      resize: vertical;
    }
    .modal-message-form input.error,
    .modal-message-form textarea.error {
      box-shadow: inset 0 0 0 2px #e57373;
    }
    .modal-message-form .error-text {
      color: #ffcdd2;
      font-size: 0.85rem;
      margin-top: 4px;
      display: block;
    }
    .message-submit-btn {
      width: 100%;
      padding: 16px;
      background: var(--cream);
      color: var(--charcoal);
      border: none;
      border-radius: 4px;
      font-size: 1.05rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      margin-top: 8px;
    }
    .message-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
    .message-form-trust {
      text-align: center;
      font-size: 0.85rem;
      color: rgba(250, 248, 245, 0.7);
      margin-top: 14px;
    }
    .form-success-modal {
      background: rgba(255, 255, 255, 0.95);
      color: var(--sage);
      padding: 18px;
      border-radius: 8px;
      text-align: center;
      font-weight: 500;
      font-size: 1rem;
    }

    /* ========== IMPACT SECTION ========== */
    .impact {
      padding: 0 20px 50px;
      position: relative;
      overflow: visible;
    }
    .impact::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: calc(-50vw + 50%);
      width: 650px;
      height: 650px;
      background-image: url('${IMAGES.bottomLeftCorner}');
      background-size: contain;
      background-repeat: no-repeat;
      opacity: 0.18;
      pointer-events: none;
      z-index: 0;
    }
    .impact-eyebrow {
      font-size: 0.8rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--terracotta);
      text-align: center;
      margin-bottom: 14px;
      position: relative;
      z-index: 1;
    }
    .impact-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.7rem;
      text-align: center;
      margin-bottom: 28px;
      position: relative;
      z-index: 1;
    }
    .impact-cards {
      display: flex;
      flex-direction: column;
      gap: 16px;
      position: relative;
      z-index: 1;
    }
    .impact-card {
      padding: 26px 22px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid var(--light-brown);
    }
    .impact-card-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.3rem;
      margin-bottom: 10px;
    }
    .impact-card-title.grow { color: var(--sage); }
    .impact-card-title.gives { color: var(--terracotta); }
    .impact-card-text {
      font-size: 0.95rem;
      color: #666;
      line-height: 1.65;
    }

    /* ========== FOOTER ========== */
    .footer {
      padding: 40px 20px;
      text-align: center;
      background: var(--charcoal);
      color: var(--cream);
    }
    .footer-mission {
      font-size: 0.75rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--terracotta);
      margin-bottom: 12px;
    }
    .footer-tagline {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.2rem;
      font-style: italic;
      opacity: 0.9;
    }

    /* ========== DISCOVER PAGE ========== */
    .story { padding: 0 20px 50px; background: #fff; }
    .story-grid { display: flex; flex-direction: column; gap: 28px; }
    .story-image {
      aspect-ratio: 4/5;
      background: #E8E4DF;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--warm-brown);
      font-size: 0.8rem;
      letter-spacing: 0.1em;
      overflow: hidden;
    }
    .story-image img { width: 100%; height: 100%; object-fit: cover; }
    .story-content p { color: #555; line-height: 1.8; margin-bottom: 20px; font-size: 1rem; }
    .story-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.6rem;
      margin-bottom: 20px;
    }
    .signature {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.1rem;
      color: var(--sage);
    }
    .signature span { font-size: 0.9rem; color: var(--warm-brown); }

    .beliefs {
      padding: 50px 20px;
      position: relative;
      overflow: hidden;
    }
    .beliefs::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -100px;
      transform: translateY(-50%);
      width: 400px;
      height: 400px;
      background-image: url('${IMAGES.floatingPads}');
      background-size: contain;
      background-repeat: no-repeat;
      opacity: 0.12;
      pointer-events: none;
      z-index: 0;
    }
    .section-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.6rem;
      text-align: center;
      margin-bottom: 32px;
      position: relative;
      z-index: 1;
    }
    .beliefs-grid {
      display: flex;
      flex-direction: column;
      gap: 28px;
      position: relative;
      z-index: 1;
    }
    .belief { text-align: center; }
    .belief h3 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.2rem;
      color: var(--sage);
      margin-bottom: 10px;
    }
    .belief p { font-size: 0.95rem; color: #666; line-height: 1.7; }

    .giveback {
      padding: 50px 20px;
      background: var(--sage);
      text-align: center;
    }
    .giveback h2 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.6rem;
      color: var(--cream);
      margin-bottom: 16px;
    }
    .giveback p {
      font-size: 1rem;
      color: rgba(250, 248, 245, 0.9);
      line-height: 1.7;
      margin-bottom: 12px;
    }
    .giveback-note {
      font-size: 0.85rem !important;
      color: rgba(250, 248, 245, 0.65) !important;
      font-style: italic;
    }

    /* ========== GIVES PAGE ========== */
    .gives-mission {
      padding: 50px 20px;
      background: #fff;
    }
    .gives-content {
      max-width: 700px;
      margin: 0 auto;
      text-align: center;
    }
    .gives-content p {
      color: #555;
      line-height: 1.8;
      margin-bottom: 20px;
      font-size: 1.05rem;
    }
    .gives-impact { padding: 50px 20px; }
    .gives-steps {
      display: flex;
      flex-direction: column;
      gap: 28px;
      max-width: 800px;
      margin: 0 auto;
    }
    .gives-step { text-align: center; }
    .gives-step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 50%;
      background: var(--terracotta);
      color: var(--cream);
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.25rem;
      margin-bottom: 12px;
    }
    .gives-step h4 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.2rem;
      margin-bottom: 8px;
    }
    .gives-step p {
      font-size: 0.95rem;
      color: #666;
      line-height: 1.65;
    }
    .gives-cta {
      padding: 60px 20px;
      background: var(--terracotta);
      text-align: center;
    }
    .gives-cta h2 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.9rem;
      color: var(--cream);
      margin-bottom: 16px;
    }
    .gives-cta p {
      color: rgba(250, 248, 245, 0.9);
      max-width: 500px;
      margin: 0 auto 24px;
      line-height: 1.7;
      font-size: 1rem;
    }
    .gives-btn {
      padding: 15px 34px;
      background: var(--cream);
      color: var(--terracotta);
      border: none;
      border-radius: 4px;
      font-family: inherit;
      font-size: 1.05rem;
      cursor: pointer;
      font-weight: 500;
    }

    /* ========== BECOME PAGE ========== */
    .benefits { padding: 0 20px 50px; }
    .benefits-grid {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .benefit {
      padding: 26px 22px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid var(--light-sage);
      text-align: center;
    }
    .benefit h3 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.25rem;
      margin-bottom: 8px;
    }
    .benefit p { font-size: 0.95rem; color: #666; line-height: 1.55; }

    .earnings { padding: 50px 20px; background: #fff; }
    .earnings-subtitle {
      text-align: center;
      color: #666;
      margin-bottom: 28px;
      font-size: 1rem;
    }
    .earnings-grid {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .earning {
      text-align: center;
      padding: 26px 22px;
      background: var(--cream);
      border-radius: 8px;
    }
    .earning-service { font-size: 0.85rem; color: var(--warm-brown); margin-bottom: 8px; }
    .earning-range {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.5rem;
      color: var(--sage);
      margin-bottom: 4px;
    }
    .earning-note { font-size: 0.85rem; color: #666; }

    .apply-form {
      padding: 50px 20px;
      background: var(--sage);
    }
    .apply-form h2 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.6rem;
      color: var(--cream);
      text-align: center;
      margin-bottom: 12px;
    }
    .apply-form > p {
      color: rgba(250, 248, 245, 0.85);
      text-align: center;
      margin-bottom: 40px;
      font-size: 1rem;
    }
    .form { max-width: 500px; margin: 0 auto; }
    .form-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 16px;
    }
    .form-group { flex: 1; min-width: 0; margin-bottom: 16px; }
    .form input, .form select, .form textarea {
      width: 100%;
      padding: 14px 16px;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-family: inherit;
      box-sizing: border-box;
    }
    .form input, .form select { height: 52px; }
    .form textarea {
      min-height: 120px;
      resize: vertical;
    }
    .form input.error, .form select.error, .form textarea.error {
      box-shadow: inset 0 0 0 2px #e57373;
    }
    .error-text {
      color: #ffcdd2;
      font-size: 0.85rem;
      margin-top: 4px;
      display: block;
    }
    .form-submit {
      width: 100%;
      padding: 16px;
      background: var(--cream);
      color: var(--sage);
      border: none;
      border-radius: 4px;
      font-size: 1.05rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      margin-top: 16px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .form-submit:disabled { opacity: 0.7; cursor: not-allowed; }
    .form-success {
      background: rgba(255, 255, 255, 0.95);
      color: var(--sage);
      padding: 18px;
      border-radius: 8px;
      text-align: center;
      margin-bottom: 20px;
      font-weight: 500;
      max-width: 500px;
      margin-left: auto;
      margin-right: auto;
      font-size: 1rem;
    }
    .form-error {
      background: #f8d7da;
      color: #721c24;
      padding: 16px;
      border-radius: 8px;
      text-align: center;
      margin-bottom: 20px;
      max-width: 500px;
      margin-left: auto;
      margin-right: auto;
      font-size: 1rem;
    }

    /* ========== CONNECT PAGE ========== */
    .how-it-works { padding: 50px 20px; background: #fff; }
    .steps {
      display: flex;
      flex-direction: column;
      gap: 28px;
      max-width: 400px;
      margin: 0 auto;
    }
    .step { text-align: center; }
    .step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 50%;
      background: var(--sage);
      color: var(--cream);
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.25rem;
      margin-bottom: 12px;
    }
    .step h4 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.2rem;
      margin-bottom: 8px;
    }
    .step p { font-size: 0.95rem; color: #666; line-height: 1.55; }

    .contact-form-section { padding: 50px 20px; }
    .contact-form { max-width: 500px; margin: 0 auto; }
    .contact-form .form-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 16px;
    }
    .contact-form .form-group { flex: 1; min-width: 0; }
    .contact-form input, .contact-form select, .contact-form textarea {
      width: 100%;
      padding: 14px 16px;
      border: 1px solid var(--light-brown);
      border-radius: 4px;
      font-size: 1rem;
      font-family: inherit;
      background: #fff;
      box-sizing: border-box;
    }
    .contact-form input, .contact-form select { height: 52px; }
    .contact-form textarea {
      min-height: 120px;
      resize: vertical;
    }
    .contact-form input.error, .contact-form select.error, .contact-form textarea.error {
      border: 2px solid #e57373;
    }
    .contact-form .error-text {
      color: #d32f2f;
      font-size: 0.85rem;
      margin-top: 4px;
      display: block;
    }
    .contact-form button {
      width: 100%;
      padding: 16px;
      background: var(--sage);
      color: var(--cream);
      border: none;
      border-radius: 4px;
      font-size: 1.05rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      margin-top: 16px;
    }
    .contact-form button:hover { background: #6b7d61; }
    .contact-form button:disabled { opacity: 0.7; cursor: not-allowed; }
    .form-trust {
      text-align: center;
      font-size: 0.9rem;
      color: var(--warm-brown);
      margin-top: 12px;
    }
    .contact-success {
      background: var(--light-sage);
      color: var(--sage);
    }
    .connect-hero { position: relative; }
    .connect-hero::after {
      content: '';
      position: absolute;
      right: -80px;
      top: 20%;
      width: 800px;
      height: 800px;
      background-image: url('${IMAGES.floatingPads}');
      background-size: contain;
      background-repeat: no-repeat;
      opacity: 0.15;
      transform: rotate(45deg);
      pointer-events: none;
      z-index: 0;
    }


    /* ========== BROWSE ALL CTA ========== */
    .browse-all-cta {
      text-align: center;
      padding: 24px 20px 0;
    }
    .browse-all-btn {
      padding: 14px 32px;
      background: var(--sage);
      color: var(--cream);
      border: none;
      border-radius: 4px;
      font-family: inherit;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s;
    }
    .browse-all-btn:hover { background: #6b7d61; }

    /* ========== DIRECTORY GRID (Gurus page) ========== */
    .directory-filters { padding-top: 10px; }
    .directory-gallery { padding: 0 20px 60px; }
    .directory-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
    }
    .guru-card-grid { cursor: pointer; }
    .guru-photo-grid {
      aspect-ratio: 9/12;
      background: #E8E4DF;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;
      overflow: hidden;
      position: relative;
      transition: transform 0.2s;
    }
    .guru-photo-grid img { width: 100%; height: 100%; object-fit: cover; }
    .guru-card-grid:active .guru-photo-grid { transform: scale(0.98); }
    .guru-specialty-pill {
      position: absolute;
      bottom: 8px;
      left: 8px;
      background: rgba(255,255,255,0.92);
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.7rem;
      color: var(--sage);
      font-weight: 500;
    }
    .guru-name-grid {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.15rem;
      font-weight: 500;
      margin-bottom: 2px;
      color: var(--charcoal);
    }
    .guru-location-grid {
      font-size: 0.85rem;
      color: var(--warm-brown);
    }
    .directory-count {
      text-align: center;
      margin-top: 28px;
      padding-top: 24px;
      border-top: 1px solid var(--light-sage);
      font-size: 0.85rem;
      color: var(--warm-brown);
    }

    /* ========== GIVE PAGE - MISSION ========== */
    .give-mission { padding: 50px 20px; background: #fff; }
    .give-mission-content {
      max-width: 700px;
      margin: 0 auto 50px;
      text-align: center;
    }
    .give-mission-content p {
      color: #555;
      line-height: 1.8;
      margin-bottom: 20px;
      font-size: 1.05rem;
    }

    /* ========== GIVE PAGE - APPLICATION FORM ========== */
    .give-application { padding: 60px 20px; background: var(--sage); }
    .give-application-inner {
      max-width: 700px;
      margin: 0 auto;
      text-align: center;
    }
    .give-application-inner .eyebrow.green { color: rgba(250, 248, 245, 0.85); }
    .give-application-inner .section-title { color: var(--cream); margin-bottom: 16px; }
    .give-application-intro {
      color: rgba(250, 248, 245, 0.9);
      font-size: 1rem;
      line-height: 1.7;
      margin-bottom: 36px;
    }
    .give-form { text-align: left; max-width: 700px; margin: 0 auto; }
    .give-form .form-row {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-bottom: 14px;
    }
    .give-form .form-group { margin-bottom: 14px; }
    .give-form input, .give-form textarea {
      width: 100%;
      padding: 14px 16px;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-family: inherit;
      background: var(--cream);
      color: var(--charcoal);
      box-sizing: border-box;
    }
    .give-form input { height: 52px; }
    .give-form textarea {
      min-height: 140px;
      resize: vertical;
    }
    .give-form input.error, .give-form textarea.error {
      box-shadow: inset 0 0 0 2px #e57373;
    }
    .give-form .error-text {
      color: #ffcdd2;
      font-size: 0.85rem;
      margin-top: 4px;
      display: block;
    }

    /* ========== GIVE PAGE - DONATE ========== */
    .give-donate { padding: 60px 20px; background: var(--cream); }
    .give-donate-inner {
      max-width: 600px;
      margin: 0 auto;
      text-align: center;
    }
    .give-donate-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.7rem;
      color: var(--terracotta);
      margin-bottom: 16px;
    }
    .give-donate-text {
      color: #555;
      line-height: 1.7;
      font-size: 1rem;
      margin-bottom: 36px;
    }
    .donate-form { text-align: left; }
    .donate-form .form-row {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-bottom: 14px;
    }
    .donate-form .form-group { margin-bottom: 14px; }
    .donate-form input, .donate-form select, .donate-form textarea {
      width: 100%;
      padding: 14px 16px;
      border: 1px solid var(--light-brown);
      border-radius: 4px;
      font-size: 1rem;
      font-family: inherit;
      background: #fff;
      color: var(--charcoal);
      box-sizing: border-box;
    }
    .donate-form input, .donate-form select { height: 52px; }
    .donate-form textarea {
      min-height: 90px;
      resize: vertical;
    }
    .donate-form input.error, .donate-form select.error, .donate-form textarea.error {
      border: 2px solid #e57373;
    }
    .donate-form .error-text {
      color: #d32f2f;
      font-size: 0.85rem;
      margin-top: 4px;
      display: block;
    }
    .donate-submit-btn {
      width: 100%;
      padding: 16px;
      background: var(--terracotta);
      color: var(--cream);
      border: none;
      border-radius: 4px;
      font-size: 1.05rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      margin-top: 16px;
    }
    .donate-submit-btn:hover { background: #b8876a; }
    .donate-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
    .donate-success {
      background: var(--light-sage);
      color: var(--sage);
    }

    /* ========== GIVEBACK BUTTON (on Discover page) ========== */
    .giveback-btn {
      padding: 14px 32px;
      background: var(--cream);
      color: var(--sage);
      border: none;
      border-radius: 4px;
      font-family: inherit;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      margin-top: 8px;
    }

    /* ========== TABLET (768px+) ========== */
    @media (min-width: 768px) {
      .nav { padding: 16px 40px; }
      .logo img { height: 40px; }
      .mobile-menu-btn { display: none; }
      .desktop-only { display: flex; gap: 32px; }
      .nav-link {
        font-size: 0.95rem;
        color: #666;
        cursor: pointer;
        padding-bottom: 4px;
        border-bottom: 2px solid transparent;
        transition: all 0.2s;
      }
      .nav-link:hover { color: var(--charcoal); }
      .nav-link.active { color: var(--charcoal); border-bottom-color: var(--sage); }

      .hero, .page-hero { padding: 90px 40px 70px; }
      .hero-title, .page-title { font-size: 3rem; }
      .hero-subtitle, .page-subtitle { font-size: 1.3rem; max-width: 620px; margin: 0 auto; }

      .filters { padding: 0 40px 30px; }
      .gallery { padding: 0 0 60px; }
      .guru-grid { padding: 0 40px 16px 40px; gap: 20px; }
      .guru-card { flex: 0 0 320px; }
      .guru-name { font-size: 1.45rem; }

      .modal-overlay { padding: 20px; display: flex; align-items: center; justify-content: center; }
      .modal {
        min-height: auto;
        max-width: 700px;
        max-height: 90vh;
        overflow-y: auto;
        border-radius: 12px;
        padding: 40px;
      }
      .modal-close { position: absolute; top: 16px; right: 16px; box-shadow: none; background: transparent; }
      .services-list { flex-direction: row; gap: 16px; }
      .service-item { flex: 1; }
      .modal-form-row { flex-direction: row; }

      .impact { padding: 0 40px 70px; }
      .impact-title { font-size: 2.1rem; }
      .impact-cards { flex-direction: row; gap: 24px; }
      .impact-card { flex: 1; }

      .footer { padding: 50px 40px; }
      .footer-tagline { font-size: 1.45rem; }

      .story { padding: 60px 40px; }
      .story-grid { flex-direction: row; gap: 50px; align-items: center; max-width: 1000px; margin: 0 auto; }
      .story-image { flex: 1; }
      .story-content { flex: 1.2; }
      .story-title { font-size: 1.9rem; }

      .beliefs { padding: 70px 40px; }
      .beliefs-grid { flex-direction: row; gap: 40px; max-width: 1000px; margin: 0 auto; }
      .belief { flex: 1; }

      .giveback { padding: 70px 40px; }
      .giveback h2 { font-size: 1.9rem; }

      .gives-steps { flex-direction: row; gap: 40px; }
      .gives-step { flex: 1; }

      .directory-gallery { padding: 0 40px 80px; }
      .directory-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
      }
      .guru-name-grid { font-size: 1.25rem; }

      .give-mission { padding: 70px 40px; }
      .give-application { padding: 80px 40px; }
      .give-form .form-row { flex-direction: row; }

      .give-donate { padding: 80px 40px; }
      .give-donate-title { font-size: 2rem; }
      .donate-form .form-row { flex-direction: row; }


      .benefits { padding: 40px 40px 60px; }
      .benefits-grid { flex-direction: row; gap: 24px; max-width: 900px; margin: 0 auto; }
      .benefit { flex: 1; }

      .earnings { padding: 60px 40px; }
      .earnings-grid { flex-direction: row; gap: 24px; max-width: 800px; margin: 0 auto; }
      .earning { flex: 1; }

      .apply-form { padding: 60px 40px; }
      .form { max-width: 700px; }
      .form-row { flex-direction: row; }

      .how-it-works { padding: 60px 40px; }
      .steps { flex-direction: row; gap: 40px; max-width: 800px; }
      .step { flex: 1; }

      .contact-form-section { padding: 60px 40px; }
      .contact-form { max-width: 600px; }
      .contact-form .form-row { flex-direction: row; }
    }

    /* ========== DESKTOP (1024px+) ========== */
    @media (min-width: 1024px) {
      .nav { padding: 20px 60px; }
      .logo img { height: 44px; }
      .nav-links { gap: 40px; }

      .hero, .page-hero { padding: 110px 60px 80px; }
      .hero-title, .page-title { font-size: 3.4rem; }

      .filters { padding: 0 60px 30px; }
      .gallery { padding: 0 0 80px; }
      .guru-grid { padding: 0 60px 16px 60px; gap: 24px; }
      .guru-card { flex: 0 0 380px; }

      .directory-gallery { padding: 0 60px 100px; }
      .directory-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;
      }

      .impact { padding: 0 60px 100px; max-width: 1000px; margin: 0 auto; }
      .impact-title { font-size: 2.4rem; }

      .footer { padding: 60px; }
      .footer-tagline { font-size: 1.55rem; }
    }

    /* ========== LARGE DESKTOP (1280px+) ========== */
    @media (min-width: 1280px) {
      .guru-card { flex: 0 0 420px; }
      .guru-grid { gap: 28px; }
      .directory-grid {
        grid-template-columns: repeat(5, 1fr);
      }
    }
  `;

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="app">
      <style>{styles}</style>
      <Navigation />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'gurus' && <GurusPage />}
      {currentPage === 'discover' && <DiscoverPage />}
      {currentPage === 'give' && <GivePage />}
      {currentPage === 'become' && <BecomePage />}
      {currentPage === 'connect' && <ConnectPage />}
      <GuruModal />
      <Footer />
    </div>
  );
}